import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { TenantPrivilegesResource } from '../../resources/tenant-privileges.resource';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, forkJoin, Observable, Subscription } from 'rxjs';
import {
  GroupTenantPermissions,
  MicrosoftDialPlanFieldConfig,
  UserTenantPermissions,
  ZpmPrivilege,
  ZpmRole,
} from '../../../shared/models/generated/ziroModels';
import { DatatableColumn, DatatableRow } from '../../../shared/tables/datatable/datatable.models';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { TranslateService } from '@ngx-translate/core';
import {
  GroupTenantPrivilegesFormData,
  PrivilegeRestriction,
  PrivilegeRole,
  PrivilegeScope,
  PrivilegesFormData,
  PrivilegeType,
  UserTenantPrivilegesFormData,
} from '../shared/privileges/privilege-models';
import { UserGroupPrivilegesModalProperties } from '../shared/privileges/user-group-privileges-modal/user-group-privileges-modal.component';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../../shared/bottom-nav/bottom-nav.service';
import { FilterSelectOption } from '../../../shared/tables/datatable/filter-select/filter-select.component';
import { MicrosoftDialPlansFieldConfigContext } from '../../../shared/contexts/microsoft-dial-plans-field-config.context';
import { ZiroSelectOption } from '../../../forms/fields/select/ziro-select.component';
import { delayWhen, map } from 'rxjs/operators';
import { UserGroupPrivilegesService } from '../shared/privileges/user-group-privileges.service';
import { ToastService } from '../../../shared/services/toast.service';
import { MicrosoftTenantsStateContext } from '../../../shared/contexts/microsoft-tenants-state.context';
import { TenantNavigationService } from '../../services/tenant-navigation.service';

interface TenantPrivilegesDatatableRow extends DatatableRow {
  type: PrivilegeType;
  name: string;
  role: PrivilegeRole;
  privileges: ZpmPrivilege[];
}

@Component({
  selector: 'ziro-tenant-privileges',
  standalone: false,
  templateUrl: 'tenant-privileges.component.html',
  styleUrls: ['../../admin-page.scss'],
  providers: [TenantNavigationService],
})
export class TenantPrivilegesComponent implements OnInit, OnDestroy {
  private _route = inject(ActivatedRoute);
  private _tenantPrivilegesResource = inject(TenantPrivilegesResource);
  private _microsoftDialPlansFieldConfigContext = inject(MicrosoftDialPlansFieldConfigContext);
  private _microsoftTenantsStateContext = inject(MicrosoftTenantsStateContext);
  private _ziroModalService = inject(ZiroModalService);
  private _translateService = inject(TranslateService);
  private _toastService = inject(ToastService);
  private _bottomNavService = inject(BottomNavService);
  private _userGroupPrivilegesService = inject(UserGroupPrivilegesService);
  private _tenantNavigationService = inject(TenantNavigationService);

  columns: DatatableColumn<TenantPrivilegesDatatableRow>[] = [
    {
      name: 'type',
      label: 'tkey;admin.privileges.table.type',
      columnCharacterWidth: 8,
      pipe: {
        transform: (value: PrivilegeType): string => this._userGroupPrivilegesService.humanReadableType(value),
      },
    },
    {
      name: 'name',
      label: 'tkey;admin.privileges.table.name',
      columnCharacterWidth: 40,
    },
    {
      name: 'role',
      label: 'tkey;admin.privileges.table.role',
      columnCharacterWidth: 25,
      pipe: {
        transform: (value: PrivilegeRole): string => this._userGroupPrivilegesService.humanReadableRole(value),
      },
    },
  ];
  rows: TenantPrivilegesDatatableRow[];
  typeFilterOptions: FilterSelectOption[] = [
    {
      value: PrivilegeType.USER,
      label: this._userGroupPrivilegesService.humanReadableType(PrivilegeType.USER),
    },
    {
      value: PrivilegeType.GROUP,
      label: this._userGroupPrivilegesService.humanReadableType(PrivilegeType.GROUP),
    },
  ];
  roleFilterOptions: FilterSelectOption[] = [
    {
      value: PrivilegeRole.TENANT_ADMINISTRATOR,
      label: this._userGroupPrivilegesService.humanReadableRole(PrivilegeRole.TENANT_ADMINISTRATOR),
    },
    {
      value: PrivilegeRole.CUSTOM,
      label: this._userGroupPrivilegesService.humanReadableRole(PrivilegeRole.CUSTOM),
    },
  ];
  privilegeFilterOptions: FilterSelectOption[] = [
    {
      value: ZpmPrivilege.TEAMS_USER_MANAGEMENT,
      label: 'tkey;admin.user_group_privileges_modal.privileges.teams_user_management',
    },
    {
      value: ZpmPrivilege.RESOURCE_ACCOUNT_MANAGEMENT,
      label: 'tkey;admin.user_group_privileges_modal.privileges.resource_account_management',
    },
    {
      value: ZpmPrivilege.AUTO_ATTENDANT_MANAGEMENT,
      label: 'tkey;admin.user_group_privileges_modal.privileges.auto_attendant_management',
    },
    {
      value: ZpmPrivilege.CALL_QUEUE_MANAGEMENT,
      label: 'tkey;admin.user_group_privileges_modal.privileges.call_queue_management',
    },
    {
      value: ZpmPrivilege.EMERGENCY_LOCATION_MANAGEMENT,
      label: 'tkey;admin.user_group_privileges_modal.privileges.emergency_location_management',
    },
    {
      value: ZpmPrivilege.CONFIG_REPORTS,
      label: 'tkey;admin.user_group_privileges_modal.privileges.configuration_reports',
    },
    {
      value: ZpmPrivilege.AUDIT_REPORTS,
      label: 'tkey;admin.user_group_privileges_modal.privileges.audit_reports',
    },
    {
      value: ZpmPrivilege.CDR_REPORTS,
      label: 'tkey;admin.user_group_privileges_modal.privileges.cdr_reports',
    },
    {
      value: ZpmPrivilege.MIGRATIONS,
      label: 'tkey;admin.user_group_privileges_modal.privileges.migration',
    },
    {
      value: ZpmPrivilege.AUTOMATION,
      label: 'tkey;admin.user_group_privileges_modal.privileges.automation',
    },
  ];
  isLoading = true;

  private _tenantId: string;
  private _tenantGuid: string;
  private _allUpns: string[];
  private _allGroupNames: string[];
  private _dialPlanGroups: MicrosoftDialPlanFieldConfig[];
  private _subscription = new Subscription();

  ngOnInit(): void {
    this._tenantId = this._route.snapshot.params.tenantId;

    const table$ = this._initTable();
    const dialPlanGroups$ = this._microsoftDialPlansFieldConfigContext.state$.pipe(
      map((dialPlanGroups) => {
        this._dialPlanGroups = dialPlanGroups;
      }),
    );
    const tenants$ = this._microsoftTenantsStateContext.fieldConfigState$.pipe(
      map((tenants) => {
        const currentTenant = tenants.privilegedTenants.find((tenant) => tenant.tenantId.toString() === this._tenantId);
        this._tenantGuid = currentTenant.tenantGuid;
        this._tenantNavigationService.buildTenantBreadcrumbs(currentTenant, [{ label: 'tkey;admin.privileges' }]);
      }),
    );

    const sub = combineLatest([table$, dialPlanGroups$, tenants$]).subscribe(() => {
      this._initBottomNav();
      this.isLoading = false;
    });
    this._subscription.add(sub);
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  private _initTable(): Observable<void> {
    return forkJoin([
      this._tenantPrivilegesResource.getUsers(this._tenantId),
      this._tenantPrivilegesResource.getGroups(this._tenantId),
    ]).pipe(
      map(([users, groups]) => {
        this._allUpns = users.map((user) => user.upn);
        this._allGroupNames = groups.map((group) => group.displayName);
        this.rows = this._buildRows(users, groups);
      }),
    );
  }

  private _buildRows(users: UserTenantPermissions[], groups: GroupTenantPermissions[]): TenantPrivilegesDatatableRow[] {
    const rows = [
      ...users.map((user) => this._mapUserToRow(user)),
      ...groups.map((group) => this._mapGroupToRow(group)),
    ];
    rows.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    return rows;
  }

  private _mapUserToRow(user: UserTenantPermissions): TenantPrivilegesDatatableRow {
    return {
      type: PrivilegeType.USER,
      name: user.upn,
      role: this._getRole(user),
      privileges: user.privileges.map((privilege) => privilege.privilege),
      actions: [
        {
          type: ButtonTypes.BUTTON,
          style: ButtonStyles.OUTLINE_DEFAULT,
          dataAutomation: 'tenant-privileges-copy-button',
          icon: ZiroIcons.COPY,
          onClick: () => {
            const formData = this._getUserModalFormData(user);
            formData.upn = '';
            this._openModal(this._allUpns, formData, false);
          },
        },
        {
          type: ButtonTypes.BUTTON,
          style: ButtonStyles.OUTLINE_PRIMARY,
          dataAutomation: 'tenant-privileges-edit-button',
          icon: ZiroIcons.EDIT,
          onClick: () => {
            const formData = this._getUserModalFormData(user);
            this._openModal(this._allUpns, formData, true);
          },
        },
        {
          type: ButtonTypes.BUTTON,
          style: ButtonStyles.OUTLINE_DANGER,
          dataAutomation: 'tenant-privileges-delete-button',
          icon: ZiroIcons.DELETE_SMALL,
          onClick: () => {
            return this._openDeleteModal(PrivilegeType.USER, user.upn, () => {
              return this._tenantPrivilegesResource.deleteUser(this._tenantId, user.upn);
            });
          },
        },
      ],
    };
  }

  private _mapGroupToRow(group: GroupTenantPermissions): TenantPrivilegesDatatableRow {
    return {
      type: PrivilegeType.GROUP,
      name: group.displayName,
      role: this._getRole(group),
      privileges: group.privileges.map((privilege) => privilege.privilege),
      actions: [
        {
          type: ButtonTypes.BUTTON,
          style: ButtonStyles.OUTLINE_DEFAULT,
          dataAutomation: 'tenant-privileges-copy-button',
          icon: ZiroIcons.COPY,
          onClick: () => {
            const formData = this._getGroupModalFormData(group);
            formData.displayName = '';
            this._openModal(this._allGroupNames, formData, false);
          },
        },
        {
          type: ButtonTypes.BUTTON,
          style: ButtonStyles.OUTLINE_PRIMARY,
          dataAutomation: 'tenant-privileges-edit-button',
          icon: ZiroIcons.EDIT,
          onClick: () => {
            const formData = this._getGroupModalFormData(group);
            this._openModal(this._allGroupNames, formData, true);
          },
        },
        {
          type: ButtonTypes.BUTTON,
          style: ButtonStyles.OUTLINE_DANGER,
          dataAutomation: 'tenant-privileges-delete-button',
          icon: ZiroIcons.DELETE_SMALL,
          onClick: () => {
            return this._openDeleteModal(PrivilegeType.GROUP, group.displayName, () => {
              return this._tenantPrivilegesResource.deleteGroup(
                this._tenantId,
                group.groupTenantGuid,
                group.displayName,
              );
            });
          },
        },
      ],
    };
  }

  private _openDeleteModal(type: PrivilegeType, name: string, cb: () => Observable<void>): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        promptBody: this._translateService.instant('tkey;admin.privileges.delete.modal.body', {
          type: this._translateService.instant(this._userGroupPrivilegesService.humanReadableType(type)),
          name: name,
        }),
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () =>
              cb().pipe(
                delayWhen(() => this._initTable()),
                map(() => {
                  const serviceType =
                    type === PrivilegeType.USER
                      ? 'tkey;admin.user_group_privileges_modal.user.toast.title'
                      : 'tkey;admin.user_group_privileges_modal.group.toast.title';
                  this._toastService.pushDeleteToast(this._translateService.instant(serviceType), name);
                }),
              ),
          },
        ],
      },
    };
    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _openModal(existingEntities: string[], entity: PrivilegesFormData, isExisting: boolean): void {
    const modalProperties: UserGroupPrivilegesModalProperties = {
      entity: entity,
      existingEntities: existingEntities,
      tenantId: this._tenantId,
      dialPlanGroups: this._dialPlanGroups,
      isExisting: isExisting,
      afterSubmitFn: () => this._initTable(),
    };
    this._userGroupPrivilegesService.openModal(modalProperties);
  }

  private _getRole(entity: UserTenantPermissions | GroupTenantPermissions): PrivilegeRole {
    return entity.roles.some((role) => role === ZpmRole.TENANT_ADMIN)
      ? PrivilegeRole.TENANT_ADMINISTRATOR
      : PrivilegeRole.CUSTOM;
  }

  private _getUserModalFormData(entity: UserTenantPermissions): UserTenantPrivilegesFormData {
    return {
      scope: PrivilegeScope.TENANT,
      type: PrivilegeType.USER,
      upn: entity.upn,
      role: this._getRole(entity),
      ...this._userGroupPrivilegesService.getModalFormDataPrivileges(
        entity.privileges.map((privilege) => privilege.privilege),
      ),
      ...this._getTeamsUserManagementRestrictedDialPlanGroups(entity),
    };
  }

  private _getGroupModalFormData(entity: GroupTenantPermissions): GroupTenantPrivilegesFormData {
    return {
      scope: PrivilegeScope.TENANT,
      type: PrivilegeType.GROUP,
      displayName: entity.displayName,
      tenantGuid: entity.groupTenantGuid,
      role: this._getRole(entity),
      ...this._userGroupPrivilegesService.getModalFormDataPrivileges(
        entity.privileges.map((privilege) => privilege.privilege),
      ),
      ...this._getTeamsUserManagementRestrictedDialPlanGroups(entity),
    };
  }

  private _getTeamsUserManagementRestrictedDialPlanGroups(entity: UserTenantPermissions | GroupTenantPermissions): {
    teamsUserManagementRestrictedDialPlanGroups: ZiroSelectOption[];
    teamsUserManagementRestrictedDialPlanGroupsAccess: PrivilegeRestriction;
  } {
    const teamsUserManagementPrivilege = entity.privileges.find(
      (privilege) => privilege.privilege === ZpmPrivilege.TEAMS_USER_MANAGEMENT,
    );
    if (teamsUserManagementPrivilege && teamsUserManagementPrivilege.restricted) {
      return {
        teamsUserManagementRestrictedDialPlanGroupsAccess: PrivilegeRestriction.PARTIAL_ACCESS,
        teamsUserManagementRestrictedDialPlanGroups: teamsUserManagementPrivilege.restrictions.map((restriction) => {
          return {
            value: restriction.entityId,
            label: this._dialPlanGroups.find((dpg) => dpg.id.toString() === restriction.entityId)?.name ?? '',
          };
        }),
      };
    }
    return {
      teamsUserManagementRestrictedDialPlanGroupsAccess: PrivilegeRestriction.FULL_ACCESS,
      teamsUserManagementRestrictedDialPlanGroups: [],
    };
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'tenant-privileges-add-user-button',
          dataAutomation: 'tenant-privileges-add-user-button',
          type: ButtonTypes.BUTTON,
          label: 'tkey;admin.privileges.add_user',
          buttonClass: ButtonStyles.PRIMARY,
          icon: ZiroIcons.ADD_USER_TO_LIST,
          cb: () => {
            const newUser: UserTenantPrivilegesFormData = {
              scope: PrivilegeScope.TENANT,
              type: PrivilegeType.USER,
              upn: '',
              role: null,
              ...this._userGroupPrivilegesService.defaultPrivileges(),
              teamsUserManagementRestrictedDialPlanGroups: [],
              teamsUserManagementRestrictedDialPlanGroupsAccess: PrivilegeRestriction.FULL_ACCESS,
            };
            this._openModal(this._allUpns, newUser, false);
          },
        },
        {
          id: 'tenant-privileges-add-group-button',
          dataAutomation: 'tenant-privileges-add-group-button',
          type: ButtonTypes.BUTTON,
          label: 'tkey;admin.privileges.add_group',
          buttonClass: ButtonStyles.PRIMARY,
          icon: ZiroIcons.ADD_USER_GROUP_TO_LIST,
          cb: () => {
            const newGroup: GroupTenantPrivilegesFormData = {
              scope: PrivilegeScope.TENANT,
              type: PrivilegeType.GROUP,
              displayName: '',
              tenantGuid: this._tenantGuid,
              role: null,
              ...this._userGroupPrivilegesService.defaultPrivileges(),
              teamsUserManagementRestrictedDialPlanGroups: [],
              teamsUserManagementRestrictedDialPlanGroupsAccess: PrivilegeRestriction.FULL_ACCESS,
            };
            this._openModal(this._allGroupNames, newGroup, false);
          },
        },
      ]),
    );
  }

  isTypeMatch(filters: string[], row: TenantPrivilegesDatatableRow): boolean {
    return !filters.length || filters.includes(row.type);
  }

  isRoleMatch(filters: string[], row: TenantPrivilegesDatatableRow): boolean {
    return !filters.length || filters.includes(row.role);
  }

  isPrivilegeMatch(filters: string[], row: TenantPrivilegesDatatableRow): boolean {
    return (
      !filters.length ||
      row.privileges.some((privilege) => filters.includes(privilege)) ||
      row.role === PrivilegeRole.TENANT_ADMINISTRATOR
    );
  }
}
